import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Select } from 'antd';
import { getTrainingList } from '../../../lib/api/training-api';
import { getModelsForAlgorithm } from '../../../lib/api/algorithm-api';
import { FormItemLabel, ModalTitle } from '../../../components/text/Titles';
import BatchUploader from '../../../common/BatchUploader';
import { Algorithm, Model, ModelVersion } from '../../../types';
import { isMultiflow } from '../../../lib/util';
import { CONFIG } from '../../../config/Config';

const { Option } = Select;

type PretrainedModel = Partial<Model>;
type Props = {
  visible: boolean;
  onClose: () => any;
  onDocumentUploaded: () => any;
  algorithm: Algorithm;
  title: string;
  version?: string;
};

const DocumentUpload = (props: Props) => {
  const { visible, onClose, onDocumentUploaded, algorithm, title, version } = props;

  const [models, setModels] = useState<(Model | PretrainedModel)[]>([]);
  const [selectedModel, setSelectedModel] = useState<null | number>(null);
  const [selectedVersion, setSelectedVersion] = useState<null | string>(null);
  const [selectedFlowName, setSelectedFlowName] = useState<null | string>(null);
  const [versions, setVersions] = useState<null | ModelVersion[]>(null);

  const fetchModels = useCallback(
    async algorithm => {
      const models = await getModelsForAlgorithm(algorithm.id);
      const modelsWithTrainings = models.filter(model => model.lastModelVersionId);

      const pretrainedModels = models.filter(model => !model.lastModelVersionId && model.hidden === false);
      pretrainedModels.map(model => (model.pretrained = true));

      return setModels([...pretrainedModels, ...modelsWithTrainings]);
    },
    [setModels]
  );

  const fetchModelVersions = useCallback(
    async selectedModel => {
      const versions = await getTrainingList(selectedModel);
      const trainedVersions = versions.filter(version => version.resultModelVersionId);
      setVersions(trainedVersions);
    },
    [setVersions]
  );

  useEffect(() => {
    fetchModels(algorithm);
  }, [fetchModels, algorithm]);

  useEffect(() => {
    if (selectedModel) {
      fetchModelVersions(selectedModel);
    }
  }, [selectedModel, fetchModelVersions]);

  useEffect(() => {
    if (!visible) {
      // reset
      setSelectedModel(null);
      setSelectedVersion(null);
      setVersions(null);
    }
  }, [visible]);

  useEffect(() => {
    if (visible && models.length && version) {
      const theModel = models.find(model => model.name?.includes(version))?.id;
      setSelectedModel(theModel || null);
    }
  }, [visible, models, version]);

  const handleModelChange = (value: number) => {
    setSelectedModel(value);
  };

  const handleFlowNameChange = (value: string) => {
    setSelectedFlowName(value);
  };

  const handleVersionChange = (value: string) => {
    setSelectedVersion(value);
  };

  const onUpload = () => {
    onDocumentUploaded();
    onClose();
  };
  const selectedModelObject = models.find(it => it.id === selectedModel);
  const isPretrainedSelectedModel = selectedModelObject && selectedModelObject.pretrained;

  return (
    <Modal
      visible={visible}
      title={<ModalTitle>{title}</ModalTitle>}
      onCancel={onClose}
      footer={
        <Button type="default" onClick={onClose}>
          Cancel
        </Button>
      }>
      <div>
        {isMultiflow(algorithm) && (
          <>
            <FormItemLabel>Select a flowName:</FormItemLabel>
            <Select
              value={selectedFlowName || undefined}
              style={{ width: '100%', marginBottom: '20px' }}
              onChange={handleFlowNameChange}>
              {CONFIG.multiflow.definitions.map(flow => (
                <Option key={flow.name} value={flow.name}>
                  {flow.name}
                </Option>
              ))}
            </Select>
            {selectedFlowName && (
              <BatchUploader
                multiple={true}
                isMultiFlow={true}
                modelId={10000000}
                modelVersion={null}
                getPresignedParams={{ customer: CONFIG.multiflow.customer, flowName: selectedFlowName }}
                getPresignedUrl={'/api/generate-presigned-urls'}
                onBatchUploaded={onUpload}
              />
            )}
          </>
        )}
        {!isMultiflow(algorithm) && (
          <>
            <FormItemLabel>Select a model:</FormItemLabel>
            <Select
              value={selectedModel || undefined}
              style={{ width: '100%', marginBottom: '20px' }}
              onChange={handleModelChange}>
              {models.map(model => (
                <Option key={model.id} value={model.id || 0}>
                  {model.name}
                </Option>
              ))}
            </Select>
            {versions && !isPretrainedSelectedModel && (
              <div>
                <FormItemLabel>Version:</FormItemLabel>
                <Select style={{ width: '100%', marginBottom: '20px' }} onChange={handleVersionChange}>
                  {versions.map(version => (
                    <Option key={version.id} value={version.resultModelVersionId}>
                      {version.modelVersionName}
                    </Option>
                  ))}
                </Select>
              </div>
            )}
            {(selectedVersion || isPretrainedSelectedModel) && (
              <>
                {algorithm.name === 'contentMatcher' ? (
                  <BatchUploader
                    multiple={true}
                    isMultiFlow={true}
                    getPresignedParams={{ customer: CONFIG.multiflow.customer, flowName: 'invoice' }}
                    getPresignedUrl={'/api/generate-presigned-urls'}
                    modelId={isPretrainedSelectedModel ? selectedModel : null}
                    modelVersion={!isPretrainedSelectedModel ? selectedVersion : null}
                    onBatchUploaded={onUpload}
                    isSplitter={false}
                    multiRun={true}
                  />
                ) : (
                  <BatchUploader
                    multiple={true}
                    modelId={isPretrainedSelectedModel ? selectedModel : null}
                    modelVersion={!isPretrainedSelectedModel ? selectedVersion : null}
                    onBatchUploaded={onUpload}
                    isSplitter={algorithm.id === 11}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default DocumentUpload;
